// V4.12 Phase E — "Meine Alerts": saved searches with alert frequency.
// Same canonical criteria as Heatmap & AI — one intelligence.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Bell, BellOff, Car, Home, Loader2, MapPin, Play, Trash2,
} from "lucide-react";
import {
  deleteSavedSearch,
  filtersToMapParams,
  listSavedSearches,
  patchSavedSearch,
  runSavedSearch,
} from "../api/savedSearchesApi";
import { criteriaChips } from "../components/alerts/SaveAlertDialog";
import AlertResultsSheet from "../components/alerts/AlertResultsSheet";

function mapUrl(item) {
  const params = filtersToMapParams(item.vertical, item.filters);
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => qs.set(k, String(v)));
  qs.set("src", "alert");
  if (item.vertical === "auto") qs.set("mode", "alle");
  return `/heatmap?${qs.toString()}`;
}

function AlertCard({ item, t, onChanged, onRunResult }) {
  const [busy, setBusy] = useState(false);
  const patch = async (payload) => {
    setBusy(true);
    try {
      await patchSavedSearch(item.ulid, payload);
      onChanged();
    } catch {
      toast.error(t("alerts.saveFailed"));
    } finally {
      setBusy(false);
    }
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const r = await runSavedSearch(item.ulid);
      onRunResult(item, r);
    } catch {
      toast.error(t("alerts.saveFailed"));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(t("alerts.confirmDelete"))) return;
    setBusy(true);
    try {
      await deleteSavedSearch(item.ulid);
      toast.success(t("alerts.deleted"));
      onChanged();
    } catch {
      toast.error(t("alerts.saveFailed"));
    } finally {
      setBusy(false);
    }
  };
  const chips = criteriaChips(item.vertical, item.filters, t);
  const VertIcon = item.vertical === "auto" ? Car : Home;

  return (
    <div
      data-testid={`alert-item-${item.ulid}`}
      className={`rounded-xl border p-4 transition-colors ${
        item.active
          ? "border-zinc-800 bg-zinc-900/40"
          : "border-zinc-900 bg-zinc-950/60 opacity-60"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <VertIcon className="h-4 w-4 text-amber-400" />
        <span className="text-sm font-semibold text-zinc-100">{item.name}</span>
        {item.new_match_count > 0 && (
          <button
            type="button"
            data-testid={`alert-new-badge-${item.ulid}`}
            title={t("alerts.markSeen")}
            onClick={() => patch({ mark_seen: true })}
            className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/25"
          >
            {t("alerts.newMatches", { count: item.new_match_count })}
          </button>
        )}
        <span className="ml-auto flex items-center gap-2">
          <select
            data-testid={`alert-frequency-select-${item.ulid}`}
            value={item.frequency}
            disabled={busy}
            onChange={(e) => patch({ frequency: e.target.value })}
            className="rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200"
          >
            <option value="daily">{t("alerts.daily")}</option>
            <option value="weekly">{t("alerts.weekly")}</option>
          </select>
          <button
            type="button"
            data-testid={`alert-toggle-${item.ulid}`}
            disabled={busy}
            title={item.active ? t("alerts.deactivate") : t("alerts.activate")}
            onClick={() => patch({ active: !item.active })}
            className={`rounded-lg border p-1.5 transition-colors ${
              item.active
                ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {item.active ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
          </button>
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <span
            key={c.key}
            className="rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[11px] text-zinc-300"
          >
            {c.label}
          </span>
        ))}
      </div>

      {item.new_matches?.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-zinc-800/70 pt-2.5 text-xs text-zinc-400">
          {item.new_matches.slice(0, 3).map((m) => (
            <li key={m.ulid} className="truncate">
              <Link
                to={`/listing/${m.ulid}`}
                className="hover:text-amber-300"
                data-testid={`alert-match-${m.ulid}`}
              >
                {m.title || m.ulid}
                {typeof m.price === "number"
                  ? ` — ${Number(m.price).toLocaleString("de-AT")} ${m.currency || "€"}`
                  : ""}
              </Link>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <Link
          to={mapUrl(item)}
          data-testid={`alert-show-map-${item.ulid}`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 px-2.5 py-1.5 text-zinc-300 transition-colors hover:border-amber-500/50 hover:text-amber-300"
        >
          <MapPin className="h-3.5 w-3.5" /> {t("alerts.showOnMap")}
        </Link>
        <button
          type="button"
          data-testid={`alert-run-${item.ulid}`}
          disabled={busy}
          onClick={runNow}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 px-2.5 py-1.5 text-zinc-300 transition-colors hover:border-amber-500/50 hover:text-amber-300"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          {t("alerts.checkNow")}
        </button>
        <button
          type="button"
          data-testid={`alert-delete-${item.ulid}`}
          disabled={busy}
          onClick={remove}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 px-2.5 py-1.5 text-zinc-500 transition-colors hover:border-red-500/50 hover:text-red-400"
        >
          <Trash2 className="h-3.5 w-3.5" /> {t("alerts.delete")}
        </button>
        <span className="ml-auto text-[11px] text-zinc-600">
          {t("alerts.lastRun")}: {item.last_run_at
            ? new Date(item.last_run_at).toLocaleString("de-AT")
            : t("alerts.never")}
        </span>
      </div>
    </div>
  );
}

export default function AlertsPage() {
  const { t } = useTranslation("common");
  const [items, setItems] = useState(null);
  const [runResult, setRunResult] = useState(null); // {alert, data}

  const load = useCallback(() => {
    listSavedSearches()
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="alerts-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2.5 text-2xl font-bold text-zinc-100">
          <Bell className="h-6 w-6 text-amber-400" />
          {t("alerts.title")}
        </h1>
        <p className="mt-1 text-sm text-zinc-400">{t("alerts.subtitle")}</p>
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" /> …
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="alerts-empty"
          className="rounded-xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500"
        >
          {t("alerts.empty")}
          <div className="mt-3 flex justify-center gap-3">
            <Link to="/heatmap" className="text-amber-300 hover:underline">
              → Heatmap
            </Link>
            <Link to="/ai" className="text-amber-300 hover:underline">
              → DealAlert AI
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <AlertCard
              key={it.ulid}
              item={it}
              t={t}
              onChanged={load}
              onRunResult={(alert, data) => setRunResult({ alert, data })}
            />
          ))}
        </div>
      )}

      {runResult && (
        <AlertResultsSheet
          alert={runResult.alert}
          result={runResult.data}
          mapHref={mapUrl(runResult.alert)}
          onClose={() => setRunResult(null)}
        />
      )}
    </div>
  );
}
